/**
 * GMA-1620 (CompassCare Web 5/14 — Clients) — client roster table.
 *
 * The Clients page's scannable roster. Rows are ordered by risk (worst severity
 * first, then online-before-offline, via the shared clientsModel) and show, per
 * client: a status badge, living environment (home + type), latest HR/SpO₂,
 * device count, last contact, and last update. Offline clients never show stale
 * vitals — their readings render as "—".
 *
 * Presentational + fail-safe. a11y: a real <table> with a caption + column
 * headers; the client cell is a <button> so rows are keyboard-operable without
 * an ambiguous clickable <tr>. Colors come from the single design-token set;
 * `offline` renders muted, distinct from clinical severities.
 */
import React from "react";
import PropTypes from "prop-types";
import SeverityBadge from "./SeverityBadge";
import { sortClientsByRisk } from "../../pages/web/clientsModel";
import { formatRelativeTime } from "../../utils/webFormat";
import { theme } from "../../styles/designTokens";
import {
  classifyVitalSeverity,
  srOnlyStyle,
  vitalRangeColor,
  vitalSeverityA11yLabel,
  vitalSeverityMarker,
} from "../../utils/vitalSeverity";

const ENV_LABELS = Object.freeze({
  group_home: "Group home",
  independent: "Independent living",
});

const th = {
  textAlign: "left",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: ".06em",
  textTransform: "uppercase",
  color: theme.textMuted,
  padding: "6px 10px",
  borderBottom: `1px solid ${theme.border}`,
};
const td = { padding: "7px 10px", borderBottom: `1px solid ${theme.border}`, fontSize: 13 };
const mono = { fontFamily: theme.fontMono };

const isNil = (x) => x === null || x === undefined;
const finiteNum = (x) => (typeof x === "number" && Number.isFinite(x) ? x : null);
const isOffline = (c) => c.online === false || !c.vitals || c.vitals.hr === 0;

function deviceCount(c) {
  if (typeof c.deviceCount === "number") return c.deviceCount;
  if (typeof c.devices === "number") return c.devices;
  return null;
}

export default function ClientsTable({ clients, now, selectedId, onSelectClient }) {
  const rows = sortClientsByRisk(clients);
  const selectable = typeof onSelectClient === "function";
  const hasAny = Array.isArray(clients) && clients.some(Boolean);

  return (
    <table
      data-testid="clients-table"
      style={{ width: "100%", borderCollapse: "collapse", fontFamily: theme.fontUI, color: theme.textPrimary }}
    >
      <caption style={{ textAlign: "left", padding: "0 0 8px", color: theme.textMuted }}>
        Clients — sorted by risk, then connectivity
      </caption>
      <thead>
        <tr>
          <th scope="col" style={th}>Client</th>
          <th scope="col" style={th}>Status</th>
          <th scope="col" style={th}>Living environment</th>
          <th scope="col" style={th}>HR</th>
          <th scope="col" style={th}>SpO₂</th>
          <th scope="col" style={th}>Dev</th>
          <th scope="col" style={th}>Last contact</th>
          <th scope="col" style={th}>Updated</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td data-testid="clients-table-empty" colSpan={8} style={{ ...td, color: theme.textMuted }}>
              {hasAny ? "No clients match this view" : "No clients"}
            </td>
          </tr>
        ) : (
          rows.map((c, index) => {
            const off = isOffline(c);
            const v = c.vitals || {};
            // Only show a reading (and color it) when it's a finite number;
            // otherwise the cell degrades to "—" rather than "undefined%"/"NaN".
            const hr = off ? null : finiteNum(v.hr);
            const spo2 = off ? null : finiteNum(v.spo2);
            const bands = c.customThresholds || c.vitalsThresholds || undefined;
            const hrLevel = hr === null ? null : classifyVitalSeverity(hr, "hr", bands);
            const spo2Level =
              spo2 === null ? null : classifyVitalSeverity(spo2, "spo2", bands);
            const dev = deviceCount(c);
            const name = c.name || "Unknown client";
            const selected = !isNil(selectedId) && c.id === selectedId;
            return (
              <tr
                key={isNil(c.id) ? `${name}-${index}` : c.id}
                data-testid="client-row"
                data-client-id={isNil(c.id) ? undefined : c.id}
                data-selected={selected ? "true" : "false"}
                style={{ background: selected ? theme.surface : "transparent" }}
              >
                <th scope="row" style={{ ...td, fontWeight: "normal" }}>
                  <button
                    type="button"
                    data-testid="client-open"
                    aria-label={`Open client ${name}`}
                    disabled={!selectable}
                    onClick={selectable ? () => onSelectClient(c) : undefined}
                    style={{
                      display: "block",
                      textAlign: "left",
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: selectable ? "pointer" : "default",
                      color: theme.textPrimary,
                      font: "inherit",
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>{name}</span>
                    {typeof c.age === "number" && (
                      <span style={{ color: theme.textMuted, marginLeft: 6, fontSize: 12 }}>{c.age}</span>
                    )}
                  </button>
                </th>
                <td style={td}>
                  {c.online === false ? (
                    <span style={{ color: theme.textMuted, fontSize: 12 }}>Offline</span>
                  ) : (
                    <SeverityBadge level={c.status} showDot={false} />
                  )}
                </td>
                <td style={td}>
                  <div>{c.home || "—"}</div>
                  <div style={{ color: theme.textMuted, fontSize: 11 }}>
                    {ENV_LABELS[c.livingEnv] || ""}
                  </div>
                </td>
                <td
                  style={{
                    ...td,
                    ...mono,
                    color: (hrLevel && vitalRangeColor(hrLevel)) || theme.textMuted,
                  }}
                  data-vital-severity={hrLevel || undefined}
                >
                  {hr === null ? "—" : hr}
                  {/* Severity is never color-only: marker + announced word. */}
                  {hrLevel && vitalSeverityMarker(hrLevel) ? (
                    <span aria-hidden="true" style={{ marginLeft: 4, fontWeight: 700 }}>
                      {vitalSeverityMarker(hrLevel)}
                    </span>
                  ) : null}
                  {hrLevel && vitalSeverityA11yLabel(hrLevel) ? (
                    <span style={srOnlyStyle}>{vitalSeverityA11yLabel(hrLevel)}</span>
                  ) : null}
                </td>
                <td
                  style={{
                    ...td,
                    ...mono,
                    color: (spo2Level && vitalRangeColor(spo2Level)) || theme.textMuted,
                  }}
                  data-vital-severity={spo2Level || undefined}
                >
                  {spo2 === null ? "—" : `${spo2}%`}
                  {spo2Level && vitalSeverityMarker(spo2Level) ? (
                    <span aria-hidden="true" style={{ marginLeft: 4, fontWeight: 700 }}>
                      {vitalSeverityMarker(spo2Level)}
                    </span>
                  ) : null}
                  {spo2Level && vitalSeverityA11yLabel(spo2Level) ? (
                    <span style={srOnlyStyle}>{vitalSeverityA11yLabel(spo2Level)}</span>
                  ) : null}
                </td>
                <td style={{ ...td, ...mono, color: theme.textMuted }}>{isNil(dev) ? "—" : dev}</td>
                <td style={{ ...td, color: theme.textMuted, fontSize: 12 }}>{c.lastContact || "—"}</td>
                <td style={{ ...td, color: theme.textMuted, fontSize: 12 }}>
                  {formatRelativeTime(c.lastUpdate, now) || "—"}
                </td>
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );
}

ClientsTable.propTypes = {
  // Clients: { id, name, age, status, livingEnv, home, vitals:{hr,spo2},
  // deviceCount, lastContact, online, lastUpdate }. Fail-safe for null/rows.
  clients: PropTypes.array,
  now: PropTypes.number,
  selectedId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  // (client) => void. Absent -> the client cells render disabled.
  onSelectClient: PropTypes.func,
};
